import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { fetchData } from "../../../api/client";
import { useAuthContext } from "../../../context/AuthContext";
import {
  can,
  toSet,
  useEffectivePermissions,
} from "../../../hooks/useEffectivePermissions";
import { workspaceScopedQueryKey } from "../../../utils/workspaceQueryScope";
import {
  mergeDeniedContext,
  parseLeadAccessDeniedPayload,
} from "../../../utils/conversationAccessDenied";
import { buildTypePermissionSettingsPath } from "../../../utils/typeSubtypeCascade";

function typeChips(denied) {
  const items = [
    denied?.typeName,
    denied?.subtype1Name,
    denied?.subtype2Name,
    denied?.subtype3Name,
  ]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean);
  if (items.length) return items;
  if (denied?.typePath) {
    return String(denied.typePath)
      .split("•")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

export default function ConversationAccessDenied({ denied, conversationId }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData } = useAuthContext();
  const { data: perms } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(perms?.scopes), [perms?.scopes]);

  const { data: accessContext } = useQuery(
    workspaceScopedQueryKey("conversationAccessContext", conversationId),
    async () => {
      const res = await fetchData(
        `/conversations/${encodeURIComponent(conversationId)}/access-context`,
        "GET"
      );
      return res?.data && typeof res.data === "object" ? res.data : res;
    },
    {
      enabled: Boolean(conversationId),
      staleTime: 15_000,
      retry: 1,
    }
  );

  const payload = useMemo(
    () =>
      mergeDeniedContext(
        typeof denied === "object" && denied && !denied.reason
          ? parseLeadAccessDeniedPayload(denied)
          : denied,
        accessContext && accessContext.allowed === false ? accessContext : null,
        accessContext && accessContext.reason ? accessContext : null
      ),
    [denied, accessContext]
  );

  const chips = typeChips(payload);
  const reason = payload.reason || "type";
  const canManageTypes = Boolean(
    payload.canManageTypePermissions ||
      Number(userData?.master) === 1 ||
      can(scopeSet, "types", "update")
  );

  const companyName = payload.projectName || null;
  const cargoName = payload.cargoName || null;
  const settingsPath =
    payload.settingsPath ||
    buildTypePermissionSettingsPath(payload.settingsEdit || {
      typeId: payload.typeId,
      subtype1: payload.subtype1,
      subtype2: payload.subtype2,
      subtype3: payload.subtype3,
    });
  const sourceName = payload.permissionSourceName || null;

  const title = "Sem permissão para ver esta solicitação";
  const typeLabel = chips.join(" • ") || "Este tipo";
  const cargoBit = cargoName ? `o cargo ${cargoName}` : "o seu cargo";
  const cargoTarget = cargoName || "este cargo";
  const companyBit = companyName ? ` na empresa ${companyName}` : "";
  const sourceBit = sourceName
    ? ` A restrição está definida em ${sourceName}.`
    : "";
  let description;
  if (reason === "project") {
    description = companyName
      ? `Você não tem acesso à empresa ${companyName}. Peça a um administrador para liberar esta empresa no seu usuário.`
      : "Você não tem acesso à empresa desta solicitação. Peça a um administrador para liberar a empresa no seu usuário.";
  } else if (reason === "visibility") {
    description =
      "Esta solicitação está com outra pessoa ou na fila, e o seu cargo não permite vê-la.";
  } else if (canManageTypes) {
    description = `${typeLabel} está restrito para ${cargoBit}${companyBit}.${sourceBit} Abra esse tipo ou subtipo e inclua ${cargoTarget} em “Cargos com permissão”.`;
  } else {
    description = `${typeLabel} está restrito para ${cargoBit}${companyBit}.${sourceBit} Peça a alguém com acesso a Configurações → Tipos para incluir ${cargoTarget} em “Cargos com permissão”.`;
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 280,
        px: 3,
        py: 4,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 560,
          borderRadius: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderColor: "warning.light",
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Stack spacing={2.25}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "warning.light",
                color: "warning.dark",
                flexShrink: 0,
              }}
            >
              <LockOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {description}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1.25} sx={{ pl: { sm: 7.25 } }}>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <AccountTreeOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Tipo e subtipos
                </Typography>
              </Stack>
              {chips.length ? (
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {chips.map((label, index) => (
                    <Chip
                      key={`${label}-${index}`}
                      label={label}
                      size="small"
                      variant={index === 0 ? "filled" : "outlined"}
                      color={index === 0 ? "warning" : "default"}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Carregando tipo da solicitação…
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
              <Stack direction="row" spacing={0.75} alignItems="center">
                <BusinessOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2">
                  <Box component="span" color="text.secondary">
                    Empresa:{" "}
                  </Box>
                  {companyName || "Carregando…"}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <BadgeOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2">
                  <Box component="span" color="text.secondary">
                    Seu cargo:{" "}
                  </Box>
                  {cargoName || "Carregando…"}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {reason !== "project" && reason !== "visibility" && canManageTypes ? (
            <Box sx={{ pl: { sm: 7.25 } }}>
              <Button
                variant="contained"
                color="warning"
                startIcon={<OpenInNewIcon />}
                onClick={() => navigate(settingsPath)}
              >
                Liberar permissão deste tipo
              </Button>
            </Box>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
